import React from 'react';
import _ from 'lodash';
import Big from 'big.js';

import {
  SaveListingManagementStrategiesByItem_StoreInput,
  useBulkUpdateListedStoresModalQuery,
  useSaveListingManagementStrategiesByItemMutation
} from 'generated/graphql';

import * as types from './types';



export interface IData {
  formState: types.IFormState;
}

export function useQuery(itemId: string) {
  const tuple = useBulkUpdateListedStoresModalQuery({
    variables: {
      itemId
    }
  });

  return React.useMemo(
    () => {
      const storeId2ListingCount = _.groupBy(tuple.data?.item?.listings.nodes ?? [], listing => listing.store.id);
      const storeId2Strategy = _.keyBy(tuple.data?.item?.listingManagementStrategies ?? [], strategy => strategy.store.id);

      const data: IData = {
        formState: {
          stores: _.map(tuple.data?.stores.nodes ?? [], (store): types.IEligibleStore => {
            const defaultPriceDollars = storeId2Strategy[store.id]?.priceDollars ?? tuple.data?.item?.defaultPriceDollars;
            return {
              canEnable: storeId2ListingCount[store.id]?.length === 1 && !store.isInventoryManagementDisabled,
              isActive: !_.isNil(storeId2Strategy[store.id]),
              storeName: store.name,
              storeId: store.id,
              priceDollars: new Big(defaultPriceDollars || 0).toFixed(2)
            };
          })
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSaveListingManagementStrategiesByItem(itemId: string) {
  const [fnMutate, tuple] = useSaveListingManagementStrategiesByItemMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState, storeIdsToDisable: string[]) => {
      await fnMutate({
        variables: {
          input: {
            storesToDisable: storeIdsToDisable,
            itemId,
            storesToEnable: formState.stores.filter(s => s.isActive).map((store): SaveListingManagementStrategiesByItem_StoreInput => ({
              storeId: store.storeId,
              priceDollars: store.priceDollars
            }))
          }
        }
      });
    },
    [
      fnMutate,
      itemId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
